/**
 * TimeLogsPanel — log time against a task.
 *
 * Props:
 *   sidebarMode  — when true, renders compact inside the task-detail sidebar
 *                  (no outer card wrapper, icon-only delete, small inputs)
 *
 * Two entry modes:
 *   "range"  — pick start time + end time; duration auto-calculated
 *   "manual" — enter hours (decimal) directly
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../lib/api';
import { formatHours, formatHoursMinutes, formatDate, formatExactEntry, formatDateTime, memberDisplayName, initials } from '../../../lib/format';

function fmtDuration(minutes) {
  const totalSecs = Math.round((minutes || 0) * 60);
  if (totalSecs === 0) return '0 sec';
  const h   = Math.floor(totalSecs / 3600);
  const m   = Math.floor((totalSecs % 3600) / 60);
  const s   = totalSecs % 60;
  const parts = [];
  if (h > 0) parts.push(`${h} hr${h !== 1 ? 's' : ''}`);
  if (m > 0) parts.push(`${m} min${m !== 1 ? 's' : ''}`);
  if (s > 0) parts.push(`${s} sec`);
  return parts.join(' ');
}

function calcDuration(start, end) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60; // overnight
  return mins;
}

export default function TimeLogsPanel({ appId, taskId, members, me, canManage, onChanged, reloadTrigger, sidebarMode }) {
  const api = useApi();
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Form state
  const [adding, setAdding]   = useState(false);
  const [visibleLogs, setVisibleLogs] = useState(3);
  const [mode, setMode]       = useState('range');   // 'range' | 'manual'
  const [startTime, setStart] = useState('09:00');
  const [endTime, setEnd]     = useState('17:00');
  const [hours, setHours]     = useState(''); // manual-mode value, in minutes
  const [loggedOn, setOn]     = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes]     = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.listTimeLogs(appId, taskId);
      if (mountedRef.current) setItems(r.items || []);
    } catch (err) {
      if (mountedRef.current) {
        const msg = err.response?.data?.error
          || (err.response ? `Server error ${err.response.status}` : 'Could not load time logs');
        setError(msg);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [api, appId, taskId]);

  useEffect(() => { load(); }, [load]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (reloadTrigger > 0) load(); }, [reloadTrigger]);

  const memberName = (id) => {
    if (id == null) return 'Someone';
    const u = members.find((m) => m.user_id === id);
    if (u) return memberDisplayName(u.first_name, u.last_name, u.email);
    // Fallback: use the current user's profile if it matches
    if (me && me.id === id) {
      return memberDisplayName(me.firstName, me.lastName, me.email) || 'You';
    }
    return `User #${id}`;
  };

  const memberPhoto = (id) => {
    if (id == null) return null;
    return members.find((m) => m.user_id === id)?.photo_url || null;
  };

  const totalMinutes = items.reduce((s, l) => s + (l.duration_minutes || 0), 0);
  const previewMins  = mode === 'range' ? calcDuration(startTime, endTime) : null;

  const handleAdd = async () => {
    setPosting(true);
    setError(null);
    try {
      if (!notes.trim()) { setError('Notes / comment is required'); setPosting(false); return; }
      if (mode === 'range') {
        if (!startTime || !endTime) { setError('Enter both start and end time'); setPosting(false); return; }
        const rangeMins = calcDuration(startTime, endTime);
        if (rangeMins > 120) { setError('Cannot log more than 2 hrs at once. Log 2 hrs max per entry.'); setPosting(false); return; }
        await api.addTimeLog(appId, taskId, {
          start_time: startTime,
          end_time:   endTime,
          logged_on:  loggedOn,
          notes:      notes.trim(),
        });
      } else {
        const m = Number(hours);
        if (!Number.isFinite(m) || m <= 0) { setError('Enter minutes (e.g. 5)'); setPosting(false); return; }
        if (m > 120) { setError('Cannot log more than 2 hrs (120 min) at once.'); setPosting(false); return; }
        await api.addTimeLog(appId, taskId, {
          duration_minutes: Math.round(m),
          logged_on: loggedOn,
          notes:     notes.trim(),
        });
      }
      setHours(''); setNotes('');
      setOn(new Date().toISOString().slice(0, 10));
      setAdding(false);
      await load();
      await onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this time log entry?')) return;
    try {
      await api.removeTimeLog(appId, taskId, id);
      await load();
      await onChanged?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const cancelAdd = () => { setAdding(false); setHours(''); setNotes(''); setError(null); };

  /* ─────────────────────────────────────────────────────────
     SIDEBAR (compact) mode
     ───────────────────────────────────────────────────────── */
  if (sidebarMode) {
    return (
      <div className="tdv2-panel">
        {/* Header */}
        <div className="tdv2-panel-head">
          <span className="tdv2-panel-title">
            ⏱ Time Logs
            {totalMinutes > 0 && (
              <span className="tdv2-panel-total" style={{ marginLeft: 5 }}>
                · {formatHoursMinutes(totalMinutes)} total
              </span>
            )}
          </span>
          {!adding && (
            <button className="tdv2-panel-add-btn" onClick={() => setAdding(true)}>+ Add</button>
          )}
        </div>

        {error && <div className="banner banner-error" style={{ marginBottom: 6, padding: '4px 8px', fontSize: 11 }}>{error}</div>}

        {/* Compact add form */}
        {adding && (
          <div className="tdv2-add-form">
            {/* Mode toggle */}
            <div className="tdv2-mode-tabs">
              <button type="button" className={`tdv2-mode-tab${mode === 'range'  ? ' active' : ''}`} onClick={() => setMode('range')}>Start→End</button>
              <button type="button" className={`tdv2-mode-tab${mode === 'manual' ? ' active' : ''}`} onClick={() => setMode('manual')}>Minutes</button>
            </div>

            {mode === 'range' ? (
              <>
                <div className="tdv2-add-form-row">
                  <input type="time" className="form-input" style={{ flex: 1 }}
                    value={startTime} onChange={e => setStart(e.target.value)} />
                  <span style={{ color: '#9ca3af', fontSize: 11 }}>→</span>
                  <input type="time" className="form-input" style={{ flex: 1 }}
                    value={endTime} onChange={e => setEnd(e.target.value)} />
                </div>
                <div className="tdv2-add-form-row">
                  <input type="date" className="form-input" style={{ flex: 1 }}
                    value={loggedOn} onChange={e => setOn(e.target.value)} />
                  {previewMins != null && (
                    <span style={{ fontSize: 11, color: '#1a5276', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {formatHours(previewMins)}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div className="tdv2-add-form-row">
                <input type="number" className="form-input" placeholder="Minutes (e.g. 5)" style={{ flex: 1 }}
                  step="1" min="1" max="120" value={hours}
                  onChange={e => setHours(e.target.value)} autoFocus />
                <input type="date" className="form-input" style={{ flex: 1 }}
                  value={loggedOn} onChange={e => setOn(e.target.value)} />
              </div>
            )}

            <input type="text" className="form-input" placeholder="Note *"
              value={notes} onChange={e => setNotes(e.target.value)} maxLength={500}
              style={{ marginBottom: 0 }} />

            <div className="tdv2-add-actions">
              <button className="btn-secondary" style={{ fontSize: 11, padding: '3px 9px' }}
                onClick={cancelAdd} disabled={posting}>Cancel</button>
              <button className="btn-primary" style={{ fontSize: 11, padding: '3px 9px' }}
                onClick={handleAdd} disabled={posting || (mode === 'manual' && !hours)}>
                {posting ? 'Logging…' : 'Log'}
              </button>
            </div>
          </div>
        )}

        {/* Log list */}
        {loading ? (
          <p className="muted" style={{ fontSize: 11, margin: '4px 0' }}>Loading…</p>
        ) : items.length === 0 ? (
          <p className="muted" style={{ fontSize: 11, margin: '4px 0' }}>No time logged yet.</p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {items.slice(0, visibleLogs).map((l) => {
              const isAuthor  = me && l.user_id === me.id;
              const canModify = isAuthor || canManage;
              const name      = memberName(l.user_id);
              const photo     = memberPhoto(l.user_id);
              return (
                <li key={l.id} style={{ display: 'flex', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
                  {/* Avatar */}
                  <div className="tdv2-avatar sm" title={name}>
                    {photo
                      ? <img src={photo} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      : initials(name)}
                  </div>
                  {/* Detail */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Row 1: name + hours */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ fontWeight: 700, fontSize: 11, color: 'var(--text-primary)' }}>{name}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand)', marginLeft: 'auto', flexShrink: 0 }}>{fmtDuration(l.duration_minutes)}</span>
                    </div>
                    {/* Row 2: work date + time range */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-primary)', fontWeight: 600 }}>📅 {formatDate(l.logged_on)}</span>
                      {l.start_time && l.end_time && (
                        <span style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 500 }}>· {l.start_time}–{l.end_time}</span>
                      )}
                    </div>
                    {/* Row 3: logged at (exact entry time) */}
                    {l.created_at && (
                      <div style={{ marginTop: 1 }}>
                        <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}
                          title={formatDateTime(l.created_at)}>
                          Logged {formatExactEntry(l.created_at)}
                        </span>
                      </div>
                    )}
                    {/* Row 4: notes */}
                    {l.notes && (
                      <div style={{ marginTop: 2 }}>
                        <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.notes}>
                          "{l.notes}"
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Delete icon */}
                  {canModify && (
                    <button className="btn-icon-only danger" title="Delete entry" onClick={() => handleDelete(l.id)}
                      style={{ marginTop: 2 }}>🗑</button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {!loading && items.length > visibleLogs && (
          <div style={{ marginTop: 6, marginLeft: 30 }}>
            <button
              style={{ fontSize: 11, background: 'none', border: 'none', color: 'var(--brand)', cursor: 'pointer', padding: 0, fontWeight: 600 }}
              onClick={() => setVisibleLogs(items.length)}
            >
              View More
            </button>
          </div>
        )}
        {!loading && visibleLogs >= items.length && items.length > 3 && (
          <div style={{ marginTop: 6, marginLeft: 30 }}>
            <button
              style={{ fontSize: 11, background: 'none', border: 'none', color: 'var(--brand)', cursor: 'pointer', padding: 0, fontWeight: 600 }}
              onClick={() => setVisibleLogs(3)}
            >
              View Less
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────
     NORMAL (full-width) mode — original layout
     ───────────────────────────────────────────────────────── */
  return (
    <section className="task-section">
      <div className="task-section-head">
        <h2>Time logs <span className="muted small">· total {formatHoursMinutes(totalMinutes)}</span></h2>
        {!adding && (
          <button className="btn-primary small" onClick={() => setAdding(true)}>
            + Log time
          </button>
        )}
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {adding && (
        <div className="inline-add-card">
          <div className="timelog-mode-tabs">
            <button type="button" className={`timelog-mode-tab${mode === 'range'  ? ' active' : ''}`} onClick={() => setMode('range')}>Start → End time</button>
            <button type="button" className={`timelog-mode-tab${mode === 'manual' ? ' active' : ''}`} onClick={() => setMode('manual')}>Enter minutes</button>
          </div>

          {mode === 'range' ? (
            <div className="timelog-range-row">
              <label className="timelog-field">
                <span>Start</span>
                <input type="time" className="form-input" value={startTime} onChange={e => setStart(e.target.value)} />
              </label>
              <label className="timelog-field">
                <span>End</span>
                <input type="time" className="form-input" value={endTime} onChange={e => setEnd(e.target.value)} />
              </label>
              <label className="timelog-field">
                <span>Date</span>
                <input type="date" className="form-input" value={loggedOn} onChange={e => setOn(e.target.value)} />
              </label>
              <div className="timelog-field timelog-duration-preview">
                <span>Duration</span>
                <strong>{previewMins != null ? formatHours(previewMins) : '—'}</strong>
              </div>
            </div>
          ) : (
            <div className="three-col">
              <input type="number" className="form-input" placeholder="Minutes (e.g. 5)"
                step="1" min="1" max="120" value={hours}
                onChange={e => setHours(e.target.value)} autoFocus />
              <input type="date" className="form-input" value={loggedOn}
                onChange={e => setOn(e.target.value)} />
            </div>
          )}

          <input type="text" className="form-input" placeholder="Note *"
            value={notes} onChange={e => setNotes(e.target.value)} maxLength={500}
            style={{ marginTop: 8 }} />

          <div className="form-actions">
            <button className="btn-secondary" onClick={cancelAdd} disabled={posting}>Cancel</button>
            <button className="btn-primary" onClick={handleAdd}
              disabled={posting || (mode === 'manual' && !hours)}>
              {posting ? 'Logging…' : 'Log time'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">No time logged yet.</p>
      ) : (
        <ul className="time-log-list">
          {items.map((l) => {
            const isAuthor  = me && l.user_id === me.id;
            const canModify = isAuthor || canManage;
            return (
              <li key={l.id} className="time-log-row">
                <div className="time-log-who">
                  <span className="time-log-user">{memberName(l.user_id)}</span>
                  {l.created_at && (
                    <span className="time-log-logged-at" title={formatDateTime(l.created_at)}>
                      Logged {formatExactEntry(l.created_at)}
                    </span>
                  )}
                </div>
                <div className="time-log-when">
                  <span className="time-log-date">📅 {formatDate(l.logged_on)}</span>
                  {l.start_time && l.end_time && (
                    <span className="time-log-range">{l.start_time}–{l.end_time}</span>
                  )}
                </div>
                <span className="time-log-hours"><strong>{fmtDuration(l.duration_minutes)}</strong></span>
                <span className="time-log-notes">{l.notes || <span className="muted">—</span>}</span>
                {canModify && (
                  <button className="btn-link-sm danger small" onClick={() => handleDelete(l.id)}>×</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
